import { type Icon, TextColumns } from "@phosphor-icons/react";
import { useMemo } from "react";

import { Button } from "common/components/ui/Button";
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerTrigger,
} from "common/components/ui/Drawer";
import { typedObjectEntries } from "common/helpers/utils";

function areEqualSets<ItemKey extends string>(
  currentKeys: ItemKey[],
  baselineKeys: ItemKey[],
) {
  if (currentKeys.length !== baselineKeys.length) return false;

  const currentKeySet = new Set(currentKeys);

  if (currentKeySet.size !== baselineKeys.length) return false;

  for (const baselineKey of baselineKeys) {
    if (!currentKeySet.has(baselineKey)) return false;
  }

  return true;
}

export default function TagSelectionDrawer<ItemKey extends string>({
  title,
  itemKeys,
  setItemKeys,
  allItems,
}: {
  title: string;
  itemKeys: ItemKey[];
  setItemKeys: (itemKeys: ItemKey[]) => void;
  allItems: Record<
    ItemKey,
    {
      icon: Icon;
      label: string;
      isDefault?: true;
    }
  >;
}) {
  const defaultItemKeys = useMemo(() => {
    return typedObjectEntries(allItems)
      .filter(([, config]) => config.isDefault)
      .map(([key]) => key);
  }, [allItems]);

  const hasSelectionChanged = !areEqualSets(itemKeys, defaultItemKeys);

  return (
    <Drawer>
      <DrawerTrigger asChild>
        <Button variant="outline-black" size="sm" leftIcon={<TextColumns />}>
          Items
        </Button>
      </DrawerTrigger>
      <DrawerContent>
        <DrawerHeader
          title={`${title} items`}
          actionComponent={
            <Button
              variant="link-purple"
              disabled={!hasSelectionChanged}
              onClick={() => setItemKeys(defaultItemKeys)}
            >
              Reset
            </Button>
          }
        />

        <DrawerBody>
          <div className="flex gap-2 flex-wrap">
            {typedObjectEntries(allItems).map(
              ([key, { icon: IconComponent, label }]) => {
                const isSelected = itemKeys.includes(key);

                return (
                  <Button
                    key={key}
                    variant={isSelected ? "secondary-black" : "outline-black"}
                    size="sm"
                    onClick={() => {
                      if (isSelected) {
                        setItemKeys(
                          itemKeys.filter(
                            (visibleItemKey) => visibleItemKey !== key,
                          ),
                        );
                      } else {
                        setItemKeys([...itemKeys, key]);
                      }
                    }}
                    leftIcon={<IconComponent weight="fill" />}
                  >
                    {label}
                  </Button>
                );
              },
            )}
          </div>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
